import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../constants/colors';
import { Button } from '../components/common/Button';
import { useCart } from '../context/CartContext';
import { getOptimizedImage } from '../utils/cloudinary';

const CarouselContainer = styled.div`
  width: 100%;
  margin: 32px 0;
  padding: 24px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 16px;
    margin: 20px 0;
  }
`;

const CarouselWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  position: relative;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const CarouselButton = styled.button`
  background-color: ${COLORS.PRIMARY};
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  
  &:hover {
    background-color: ${COLORS.PRIMARY_DARK || '#5a4a8a'};
    transform: scale(1.1);
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const BookDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 400px;
  width: 100%;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const BookImageContainer = styled.div`
  width: 100%;
  max-width: 300px;
  height: 400px;
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: 768px) {
    max-width: 220px;
    height: 293px;
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    max-width: 180px;
    height: 240px;
  }
`;

const BookInfo = styled.div`
  text-align: center;
  width: 100%;
  position: relative;
`;

const BookTitle = styled.h3`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${COLORS.PRIMARY};
  margin: 0 0 8px 0;
  height: 2em;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const BookPrice = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: #212529;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    gap: 10px;
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const QuantityButton = styled.button`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #e9ecef;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  
  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    font-size: 0.9rem;
  }
`;

const QuantityDisplay = styled.span`
  font-size: 1.1rem;
  font-weight: 500;
  min-width: 30px;
  text-align: center;
  
  @media (max-width: 480px) {
    font-size: 1rem;
    min-width: 25px;
  }
`;

const SuccessMessage = styled.div`
  position: absolute;
  top: -60px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #d4edda;
  color: #155724;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10;
  animation: slideDown 0.3s ease-out;
  white-space: nowrap;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  @media (max-width: 768px) {
    top: -50px;
    padding: 10px 16px;
    font-size: 0.85rem;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
  
  @media (max-width: 480px) {
    top: -45px;
    padding: 8px 12px;
    font-size: 0.8rem;
  }
`;

const CarouselIndicators = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  
  @media (max-width: 768px) {
    margin-top: 12px;
    gap: 6px;
  }
`;

const Indicator = styled.button<{ active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.active ? COLORS.PRIMARY : '#dee2e6'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.active ? COLORS.PRIMARY : '#adb5bd'};
  }
`;

interface Book {
  id: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  imageUrl: string;
  allowedCountries?: string[];
}

interface BookCarouselProps {
  books: Book[];
}

export const BookCarousel: React.FC<BookCarouselProps> = ({ books }) => {
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [quantities, setQuantities] = useState<Record<string, number>>(
    books.reduce((acc, book) => ({ ...acc, [book.id]: 1 }), {})
  );
  const [showSuccess, setShowSuccess] = useState<string | null>(null);

  const safeIndex = books.length
  ? Math.min(Math.max(currentIndex, 0), books.length - 1)
  : 0;
  const currentBook = books[safeIndex];

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : books.length - 1));
    setShowSuccess(null);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < books.length - 1 ? prev + 1 : 0));
    setShowSuccess(null);
  };

  const handleQuantityChange = (bookId: string, delta: number) => {
    setQuantities(prev => ({
      ...prev,
      [bookId]: Math.max(1, (prev[bookId] || 1) + delta)
    }));
  };

  const handleAddToCart = () => {
    const quantity = quantities[currentBook.id] || 1;

    addToCart(
      {
        bookId: currentBook.id,
        title: currentBook.title,
        price: currentBook.price,
        currency: currentBook.currency,
        imageUrl: currentBook.imageUrl,
        allowedCountries: currentBook.allowedCountries,
      },
      quantity,
      () => {
        setShowSuccess(currentBook.id);
        setQuantities(prev => ({ ...prev, [currentBook.id]: 1 }));
        setTimeout(() => setShowSuccess(null), 3000);
      }
    );
  };


  if (!books || books.length === 0) {
    return null;
  }

  return (
    <CarouselContainer>
      <CarouselWrapper>
        <CarouselButton
          onClick={handlePrevious}
          disabled={books.length <= 1}
          aria-label={t('bookCarousel.previousBook')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </CarouselButton>

        <BookDisplay>
          <BookImageContainer>
            <img 
              // src={currentBook.imageUrl} 
              src={getOptimizedImage(currentBook.imageUrl, 600)}
              alt={currentBook.title}
              onError={(e) => {
                e.currentTarget.src = 'https://placehold.co/300x400?text=Book+Cover';
              }}
            />
          </BookImageContainer>

          <BookInfo>
            <BookTitle>{currentBook.title}</BookTitle>
            {/* <BookDescription>{currentBook.description}</BookDescription> */}
            <BookPrice>
              {currentBook.currency}{currentBook.price.toFixed(2)}
            </BookPrice>

            <QuantitySelector>
              <QuantityButton 
                onClick={() => handleQuantityChange(currentBook.id, -1)}
                disabled={quantities[currentBook.id] <= 1}
                aria-label={t('bookCarousel.decreaseQuantity')}
              >
                −
              </QuantityButton>
              <QuantityDisplay>{quantities[currentBook.id]}</QuantityDisplay>
              <QuantityButton 
                onClick={() => handleQuantityChange(currentBook.id, 1)}
                aria-label={t('bookCarousel.increaseQuantity')}
              >
                +
              </QuantityButton>
            </QuantitySelector>

            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <Button
                variant="primary"
                size="medium"
                onClick={handleAddToCart}
              >
                {t('bookCarousel.addToCart')}
              </Button>
            </div>
            
            {showSuccess === currentBook.id && (
              <SuccessMessage>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                {t('bookCarousel.addedToCart')}
              </SuccessMessage>
            )}
          </BookInfo>
        </BookDisplay>

        <CarouselButton
          onClick={handleNext}
          disabled={books.length <= 1}
          aria-label={t('bookCarousel.nextBook')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </CarouselButton>
      </CarouselWrapper>

      {books.length > 1 && (
        <CarouselIndicators>
          {books.map((_, index) => (
            <Indicator
              key={index}
              active={index === currentIndex}
              onClick={() => {
                setCurrentIndex(index);
                setShowSuccess(null);
              }}
              aria-label={t('bookCarousel.goToBook', { number: index + 1 })}
            />
          ))}
        </CarouselIndicators>
      )}
    </CarouselContainer>
  );
};